// How a language sounds, as far as the app needs to know.
//
// Mynah's cards are built around Mandarin: a reading in pinyin, a tone per syllable, a colour per
// tone, and a guide to initials, finals and the rules that change them in running speech. That is
// 395 lines of knowledge about one language sitting in the middle of an app that is meant to teach
// several, and the reverse pair (MYN-102) showed what it costs — a Pronunciation section about
// Mandarin in a build that teaches English, and a tone drill with nothing to drill.
//
// WHAT THE CARD ACTUALLY NEEDS is smaller than what Mandarin has: split a reading into syllables,
// and — if this language has tones at all — say which tone each one carries so it can be coloured.
// Everything else is the guide, which is either there or is not.
//
// So most languages get `tones: null` and `sounds: null`, and the app leaves out what they have
// nothing to put in rather than rendering an empty version of it.

import {
  bestVoice as bestChineseVoice,
  hasSandhi,
  rankVoices as rankChineseVoices,
  qualityScore,
  spokenTones,
  stripTones,
  syllablesOf,
  TONES,
  toneInfo,
  type ToneInfo,
  type VoiceLike,
} from './pinyin.js';
import { FINALS, INITIALS, SANDHI, type SandhiRule, type SoundGroup } from './pronunciation.js';

export type { ToneInfo, VoiceLike };

/** A language whose syllables carry pitch. Mandarin has this; most languages do not. */
export interface Tones {
  /** Every tone, in order, for the guide's table. */
  all: ToneInfo[];
  /** What tone `n` is. */
  describe(n: number): ToneInfo;
  /**
   * The tones AS SPOKEN, which is not always as written.
   *
   * Mandarin raises the first of two third tones. The writing keeps the original mark, so a card
   * that colours by the written tone teaches a sound nobody makes.
   */
  spoken(tones: number[]): number[];
  /** Does this run of tones change when it is said aloud? */
  changes(tones: number[]): boolean;
  /** The reading with its tone marks removed, for a drill that hides the answer. */
  strip(reading: string): string;
  /** The rules behind `spoken`, for the guide to explain. */
  rules: SandhiRule[];
}

/** The sounds of a language, laid out for the guide. Null when this build has no guide. */
export interface Sounds {
  initials: SoundGroup[];
  finals: SoundGroup[];
}

export interface Phonology {
  /** The language this is for, as a primary subtag: "zh", "en". */
  code: string;
  /** Split a written reading into its syllables, for per-syllable rendering. */
  syllables(reading: string): string[];
  /** Null when the language has no tones — and then no colouring, no pills, no tone drill. */
  tones: Tones | null;
  /** Null when this build carries no pronunciation guide — and then the section is not offered. */
  sounds: Sounds | null;
  /** The device's voices for this language, best first. */
  rankVoices<T extends VoiceLike>(voices: T[]): T[];
  bestVoice<T extends VoiceLike>(voices: T[]): T | undefined;
}

/**
 * Does a reading tell the reader anything the word above it does not?
 *
 * A reading exists to say how a word is SAID where its writing does not show that: pinyin under
 * 的, furigana over 私. But a word already written in the script its own reading uses reads as
 * itself, and then the card prints the same string twice — は above は, once large and once small
 * and grey. 558 of the Japanese vocabulary's words are kana-only, including every one of the 92
 * kana the alphabet decks teach, and 61% of the word tokens in its sentences are. No Japanese
 * text has ever put furigana over kana.
 *
 * A blank reading adds nothing either, which is what the English courses carry.
 */
export function readingAdds(surface: string, reading: string): boolean {
  const said = reading.trim();
  return said !== '' && said !== surface.trim();
}

/**
 * A language the app has been told nothing special about.
 *
 * Not a stub standing in for missing work: for most languages this IS the answer. English readings
 * are the spelling, Spanish syllables are separated by spaces if they are separated at all, and
 * neither has a tone to colour. The app renders a card with no reading row and no pills, which is
 * the correct card rather than a degraded one.
 */
function plainPhonology(code: string): Phonology {
  return {
    code,
    syllables: (reading) => (reading ? reading.trim().split(/\s+/u).filter(Boolean) : []),
    tones: null,
    sounds: null,
    // Ranked on quality alone. Which language a voice speaks is already settled by the caller —
    // see speaksLearning — and preferring one accent over another is a fact about a language,
    // which is exactly the part a language nobody has written a phonology for does not have.
    rankVoices: (voices) => [...voices].sort((a, b) => qualityScore(b) - qualityScore(a)),
    bestVoice(voices) {
      return this.rankVoices(voices)[0];
    },
  };
}

const mandarin: Phonology = {
  code: 'zh',
  syllables: syllablesOf,
  tones: {
    all: TONES,
    describe: toneInfo,
    spoken: spokenTones,
    changes: hasSandhi,
    strip: stripTones,
    rules: SANDHI,
  },
  sounds: { initials: INITIALS, finals: FINALS },
  rankVoices: rankChineseVoices,
  bestVoice: bestChineseVoice,
};

/**
 * The phonology for a language.
 *
 * Matched on the primary subtag, so zh-TW and zh-HK get Mandarin's — an accent is not a different
 * language, and the tone marks mean the same thing in all of them. Anything else gets the plain
 * one, which is an answer rather than a gap.
 */
export function phonologyFor(code: string): Phonology {
  const primary = code.split('-')[0]!.toLowerCase();
  return primary === 'zh' ? mandarin : plainPhonology(primary);
}
